import React, { useContext, useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';

import { CombatContext } from '../contexts/CombatContext';
import { runtimeContextPayload } from './combatContextIdentity';
import { ensureSharingContext } from './sharingApi';
import {
  fetchContextualGeneratorCatalogue, fetchSavedGeneratorResults,
  openSpecialistSource, shareSavedGeneratorResult,
} from './provenanceApi';
import { generatorCommerceRoute, launchGeneratorInDcc } from './dccGeneratorLaunch';
import {
  ActionPill, StatusBadge, WorkspaceChip, useLayeredModal,
} from './ui/LayeredWorkspace';

const LEVELS = ['ALL', 'A', 'B', 'C', 'D'];
const PAGE_SIZE = 24;

export function generatorContextForView(activeView) {
  if (activeView === 'view-treasure') return 'after-action';
  if (['view-encounter-dashboard', 'view-random-encounters', 'view-wave-encounters', 'view-selection'].indexOf(activeView) >= 0) return 'encounter-setup';
  return 'active-combat';
}

function contextLabel(context) {
  return {
    players: 'Players', 'encounter-setup': 'Encounter Setup',
    'active-combat': 'Active Combat', 'after-action': 'After Action',
  }[context] || 'Combat PRO';
}

function SavedResult({ result, onShared }) {
  const playerFacts = (result.player_projection && result.player_projection.player_safe_facts) || [];
  const [notice, setNotice] = useState('');
  async function share() {
    try {
      await shareSavedGeneratorResult(result.id, { visibility: 'PARTY', title: result.source_generator_slug + ' result' });
      setNotice('Player-safe result shared with the party.');
      if (onShared) onShared();
    } catch (_error) { setNotice('This result has no player-safe projection to share.'); }
  }
  return <article className="cp-generator-result">
    <div><StatusBadge tone="ready">Saved</StatusBadge><strong>{result.source_product_id}</strong></div>
    <small>Revision {result.source_revision} · {result.visibility}</small>
    {playerFacts.map(function (fact, index) { return <p key={index}>{typeof fact === 'string' ? fact : JSON.stringify(fact)}</p>; })}
    <details><summary>Open saved result</summary><pre>{JSON.stringify(result.record || {}, null, 2)}</pre></details>
    {result.player_projection_available ? <ActionPill primary onClick={share}>Share Result</ActionPill> : null}
    {notice ? <span role="status">{notice}</span> : null}
  </article>;
}

function SavedResultsWorkspace({ campaignId, card }) {
  const [results, setResults] = useState([]);
  const [notice, setNotice] = useState('Loading saved results…');
  useEffect(function () {
    let live = true;
    fetchSavedGeneratorResults(campaignId, card.slug).then(function (value) {
      if (!live) return;
      setResults(value.results || []);
      setNotice((value.results || []).length ? '' : 'No saved results are available.');
    }).catch(function () { if (live) setNotice('Saved results could not be loaded.'); });
    return function () { live = false; };
  }, [campaignId, card.slug]);
  return <section className="cp-generator-results">
    {notice ? <p role="status">{notice}</p> : null}
    {results.map(function (result) { return <SavedResult key={result.id} result={result} />; })}
  </section>;
}

function GeneratorCard({ card, campaignId, openLayer, navigate, onNotice }) {
  async function openGenerator() {
    try {
      const value = await openSpecialistSource(card.slug);
      onNotice('Opening ' + card.name + ' in DCC…');
      await launchGeneratorInDcc(value);
    } catch (error) { onNotice(error && error.message === 'dcc_required' ? 'Open Generator is available in the VISINVOKE Desktop Control Centre.' : card.name + ' is not owned, available or authorised for this DCC session.'); }
  }
  function details() {
    navigate(generatorCommerceRoute(card.slug, 'info'));
  }
  function saved() {
    openLayer({ id: 'cp-generator-saved-' + card.slug, title: card.name + ' saved results', eyebrow: 'Persistent source results', content: <SavedResultsWorkspace campaignId={campaignId} card={card} /> });
  }
  return <article className="cp-generator-card" data-level={card.integration_level}>
    <div className="cp-generator-card-head"><span>Level {card.integration_level}</span><StatusBadge tone={card.owned ? 'taken' : 'unsearched'}>{card.owned ? 'Owned' : 'Not Owned'}</StatusBadge></div>
    <strong>{card.name}</strong><p>{card.owned ? card.description : card.context_benefit}</p>
    {card.dm_runtime_excluded ? <small>Protected existing DM runtime · discovery only</small> : null}
    {card.owned && card.saved_result_count ? <small>{card.saved_result_count} saved · {card.shareable_result_count} player-safe</small> : null}
    {card.owned && !card.saved_result_count ? <small>No saved result yet</small> : null}
    {!card.owned ? <small>${(Number(card.individual_price_cents || 0) / 100).toFixed(2)} {card.currency || 'USD'} / month</small> : null}
    <div className="cp-action-row">
      {card.owned && card.saved_result_count > 0 && campaignId ? <ActionPill onClick={saved}>Open</ActionPill> : null}
      {card.owned ? <ActionPill primary onClick={openGenerator}>Open Generator</ActionPill> : <><ActionPill onClick={details}>More Info</ActionPill><ActionPill primary onClick={function () { navigate(generatorCommerceRoute(card.slug, 'buy')); }}>Buy</ActionPill></>}
    </div>
  </article>;
}

function GeneratorCatalogueWorkspace({ context, campaignId, mode }) {
  const { openLayer } = useLayeredModal();
  const navigate = useNavigate();
  const [cards, setCards] = useState([]);
  const [catalogueCount, setCatalogueCount] = useState(0);
  const [browseAll, setBrowseAll] = useState(false);
  const [level, setLevel] = useState('ALL');
  const [query, setQuery] = useState('');
  const [page, setPage] = useState(1);
  const [notice, setNotice] = useState('Loading contextual generators…');

  useEffect(function () {
    let live = true;
    function load(announce) {
      if (announce) setNotice('Loading contextual generators…');
      return fetchContextualGeneratorCatalogue(context, campaignId, browseAll).then(function (value) {
      if (!live) return;
      setCards(value.cards || []); setCatalogueCount(value.catalogue_count || 0); setPage(1);
      if (context === 'active-combat' && !browseAll && !(value.cards || []).length) setNotice('No generator result is linked to active combat. Browse / add to discover relevant generators.');
      else setNotice((value.cards || []).length + ' contextual generator' + ((value.cards || []).length === 1 ? '' : 's') + '.');
      }).catch(function () { if (live && announce) setNotice('Generator catalogue could not be loaded.'); });
    }
    function refreshOwnership() { load(false); }
    function refreshWhenVisible() { if (document.visibilityState === 'visible') refreshOwnership(); }
    load(true);
    window.addEventListener('focus', refreshOwnership);
    window.addEventListener('pageshow', refreshOwnership);
    document.addEventListener('visibilitychange', refreshWhenVisible);
    return function () {
      live = false;
      window.removeEventListener('focus', refreshOwnership);
      window.removeEventListener('pageshow', refreshOwnership);
      document.removeEventListener('visibilitychange', refreshWhenVisible);
    };
  }, [context, campaignId, browseAll]);

  const filtered = useMemo(function () {
    const needle = query.trim().toLowerCase();
    return cards.filter(function (card) {
      if (level !== 'ALL' && card.integration_level !== level) return false;
      if (!needle) return true;
      return (card.name + ' ' + card.description + ' ' + card.category).toLowerCase().indexOf(needle) >= 0;
    });
  }, [cards, level, query]);
  const pages = Math.max(1, Math.ceil(filtered.length / PAGE_SIZE));
  const shown = filtered.slice((page - 1) * PAGE_SIZE, page * PAGE_SIZE);

  return <section className="cp-generator-catalogue" aria-label={contextLabel(context) + ' generators'}>
    <div className="cp-generator-toolbar"><label>Search generators<input value={query} onChange={function (event) { setQuery(event.target.value); setPage(1); }} /></label><ActionPill primary={browseAll} onClick={function () { setBrowseAll(!browseAll); }}>{browseAll ? 'Context only' : 'Browse / add'}</ActionPill></div>
    <div className="cp-generator-levels" aria-label="Integration levels">{LEVELS.map(function (item) { return <ActionPill key={item} primary={level === item} onClick={function () { setLevel(item); setPage(1); }}>{item === 'ALL' ? 'All levels' : 'Level ' + item}</ActionPill>; })}</div>
    <p role="status">{notice} {browseAll ? 'Browsing the full classified catalogue (' + catalogueCount + '); 24 cards per page.' : ''}</p>
    <div className="cp-generator-grid">{shown.map(function (card) { return <GeneratorCard key={card.slug} card={card} campaignId={mode === 'dm' ? campaignId : null} openLayer={openLayer} navigate={navigate} onNotice={setNotice} />; })}</div>
    {pages > 1 ? <nav className="cp-generator-pages" aria-label="Generator pages"><ActionPill disabled={page <= 1} onClick={function () { setPage(page - 1); }}>Previous</ActionPill><span>Page {page} of {pages}</span><ActionPill disabled={page >= pages} onClick={function () { setPage(page + 1); }}>Next</ActionPill></nav> : null}
  </section>;
}

export function GeneratorCatalogueChip({ mode = 'dm', context = 'encounter-setup', campaignId }) {
  const { openLayer } = useLayeredModal();
  const combat = useContext(CombatContext);
  const [identity, setIdentity] = useState(null);
  useEffect(function () {
    if (mode !== 'dm' || !combat || !combat.state) return undefined;
    let live = true;
    ensureSharingContext(runtimeContextPayload(combat.state)).then(function (value) { if (live) setIdentity(value); }).catch(function () {});
    return function () { live = false; };
  }, [mode, combat]);
  const effectiveCampaign = campaignId || (identity && identity.campaign_id);
  function open() {
    openLayer({
      id: 'cp-generator-catalogue-' + mode, title: contextLabel(context) + ' Generators',
      eyebrow: 'Contextual generator catalogue',
      subtitle: 'Real ownership, saved-result discovery and approved integration levels. Specialist generators remain their own products.',
      content: <GeneratorCatalogueWorkspace context={context} campaignId={effectiveCampaign} mode={mode} />,
    });
  }
  return <WorkspaceChip label={'Open ' + contextLabel(context) + ' Generators'} onClick={open}>Generators</WorkspaceChip>;
}

export default GeneratorCatalogueChip;
